export type OfferEntryInput = {
  requestNo: string;
  supplierId: string;
  unitPrice: string;
  currency: "TRY" | "EUR" | "USD";
  paymentDays: string;
  deliveryDays: string;
  procurementNote: string;
  validUntil: string;
  shippingCost?: string;
  insuranceCost?: string;
  installationCost?: string;
  otherCost?: string;
  otherCostDescription?: string;
};

export type ExistingSupplierOffer = {
  id: string;
  requestNo: string;
  supplierId: string;
  offerNo?: string | null;
};

export function existingOfferReference(offer: ExistingSupplierOffer) {
  const offerNo = offer.offerNo?.trim();
  return offerNo && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(offerNo)
    ? `${offerNo} numaralı teklif`
    : "mevcut teklif";
}

export function findDuplicateSupplierOffer<T extends ExistingSupplierOffer>(
  offers: T[],
  requestNo: string,
  supplierId: string,
  editingOfferId?: string | null,
) {
  if (!requestNo || !supplierId) return null;
  return (
    offers.find(
      (offer) =>
        offer.requestNo === requestNo &&
        offer.supplierId === supplierId &&
        offer.id !== editingOfferId,
    ) ?? null
  );
}

export function resolveOfferEditingId(
  editingOfferId: string | null,
  originalSupplierId: string,
  selectedSupplierId: string,
) {
  if (
    editingOfferId &&
    originalSupplierId &&
    selectedSupplierId !== originalSupplierId
  ) {
    return null;
  }
  return editingOfferId;
}

export function validateOfferEntry(input: OfferEntryInput) {
  if (
    !input.requestNo ||
    !input.supplierId ||
    !input.unitPrice.trim() ||
    !input.paymentDays ||
    !input.deliveryDays ||
    !input.validUntil
  ) {
    return "Tedarikçi, birim fiyat, vade, teslimat süresi ve geçerlilik tarihi zorunludur.";
  }
  if (
    !Number.isFinite(Number(input.unitPrice)) ||
    Number(input.unitPrice) <= 0
  ) {
    return "Birim fiyat sıfırdan büyük olmalıdır.";
  }
  if (
    !Number.isInteger(Number(input.paymentDays)) ||
    Number(input.paymentDays) < 0 ||
    !Number.isInteger(Number(input.deliveryDays)) ||
    Number(input.deliveryDays) < 0
  ) {
    return "Vade ve teslimat süresi sıfır veya pozitif tam sayı olmalıdır.";
  }
  return null;
}

export function calculateOfferTotal(
  unitPrice: string | number,
  requestedQuantity: string | number,
) {
  const price = Number(String(unitPrice).trim().replace(",", "."));
  const quantity = Number(String(requestedQuantity).trim().replace(",", "."));
  if (
    !Number.isFinite(price) ||
    price <= 0 ||
    !Number.isFinite(quantity) ||
    quantity <= 0
  ) {
    return null;
  }
  return Number((price * quantity).toFixed(2));
}

export function parseOfferAmountValue(value: string | number) {
  if (typeof value === "number") return value;

  const trimmed = value.trim();
  const normalized = trimmed.replace(/[^\d,.-]/g, "");
  if (!normalized) return Number.NaN;

  if (normalized.includes(",")) {
    return Number(normalized.replaceAll(".", "").replace(",", "."));
  }

  const dotCount = (normalized.match(/\./g) ?? []).length;
  const isFormattedThousands =
    /[^\d.,-]/.test(trimmed) && /^-?\d{1,3}(\.\d{3})+$/.test(normalized);
  if (dotCount > 1 || isFormattedThousands) {
    return Number(normalized.replaceAll(".", ""));
  }

  return Number(normalized);
}

const DAY_IN_MILLISECONDS = 86_400_000;

function dateOnlyTimestamp(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return timestamp;
}

function localTodayTimestamp(today: Date | string) {
  if (typeof today === "string") return dateOnlyTimestamp(today);
  if (Number.isNaN(today.getTime())) return null;
  return Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
}

function formatDateOnly(timestamp: number) {
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(timestamp));
}

export function getOfferDeliveryWarning(
  deliveryDays: string | number,
  neededAt: string,
  today: Date | string = new Date(),
) {
  if (String(deliveryDays).trim() === "") return null;
  const days = Number(deliveryDays);
  const neededTimestamp = dateOnlyTimestamp(neededAt);
  const todayTimestamp = localTodayTimestamp(today);
  if (
    !Number.isInteger(days) ||
    days < 0 ||
    neededTimestamp === null ||
    todayTimestamp === null
  ) {
    return null;
  }
  const estimatedTimestamp = todayTimestamp + days * DAY_IN_MILLISECONDS;
  if (estimatedTimestamp <= neededTimestamp) return null;
  const lateByDays = Math.round(
    (estimatedTimestamp - neededTimestamp) / DAY_IN_MILLISECONDS,
  );
  return {
    lateByDays,
    estimatedDeliveryAt: new Date(estimatedTimestamp)
      .toISOString()
      .slice(0, 10),
    message: `Bugün sipariş verilse tahmini teslim ${formatDateOnly(estimatedTimestamp)}. İhtiyaç tarihi ${formatDateOnly(neededTimestamp)}; ${lateByDays} gün gecikme riski var.`,
  };
}
